import { useQuery } from '@tanstack/react-query';
import { costService } from './service';
import type { CostGroupBy } from './types';

/**
 * L8 — react-query cache keys for cost reads.
 *
 * Naming follows the convention used elsewhere in the app:
 *   ['cost', scope, ...identifying-params]
 * so `queryClient.invalidateQueries({ queryKey: ['cost'] })` clears the
 * entire cost cache after a write that may affect any aggregate (rate
 * edits, new consumption, etc.).
 */
const KEYS = {
  all: ['cost'] as const,
  workOrder: (id: string) => [...KEYS.all, 'workOrder', id] as const,
  vessel: (
    id: string,
    from?: Date,
    to?: Date,
    groupBy?: CostGroupBy,
  ) =>
    [
      ...KEYS.all,
      'vessel',
      id,
      from?.toISOString() ?? null,
      to?.toISOString() ?? null,
      groupBy ?? null,
    ] as const,
  company: (
    id: string,
    from?: Date,
    to?: Date,
    groupBy?: CostGroupBy,
  ) =>
    [
      ...KEYS.all,
      'company',
      id,
      from?.toISOString() ?? null,
      to?.toISOString() ?? null,
      groupBy ?? null,
    ] as const,
};

export function useWorkOrderCost(workOrderId: string | undefined) {
  return useQuery({
    queryKey: workOrderId ? KEYS.workOrder(workOrderId) : ['cost', 'idle'],
    queryFn: () => costService.getWorkOrderCost(workOrderId!),
    enabled: Boolean(workOrderId),
  });
}

export function useVesselCost(input: {
  vesselId: string | undefined;
  from?: Date;
  to?: Date;
  groupBy?: Extract<CostGroupBy, 'month' | 'category'>;
}) {
  return useQuery({
    queryKey: input.vesselId
      ? KEYS.vessel(input.vesselId, input.from, input.to, input.groupBy)
      : ['cost', 'idle'],
    queryFn: () =>
      costService.getVesselCost({
        vesselId: input.vesselId!,
        from: input.from,
        to: input.to,
        groupBy: input.groupBy,
      }),
    enabled: Boolean(input.vesselId),
  });
}

export function useCompanyCost(input: {
  companyId: string | undefined;
  from?: Date;
  to?: Date;
  groupBy?: Extract<CostGroupBy, 'vessel' | 'month' | 'woType'>;
}) {
  return useQuery({
    queryKey: input.companyId
      ? KEYS.company(input.companyId, input.from, input.to, input.groupBy)
      : ['cost', 'idle'],
    queryFn: () =>
      costService.getCompanyCost({
        companyId: input.companyId!,
        from: input.from,
        to: input.to,
        groupBy: input.groupBy,
      }),
    enabled: Boolean(input.companyId),
  });
}

export const costQueryKeys = KEYS;
